import { httpClient } from '@/lib/httpClient';

export enum Language {
  'PT-BR' = 'PT-BR',
  'EN-US' = 'EN-US',
  'ES-CO' = 'ES-CO',
}

export enum Platform {
  NONE = 'NONE',
  VTEX = 'VTEX',
  SHOPIFY = 'SHOPIFY',
}

export interface CreateCustomerDto {
  name: string;
  platform: Platform;
  url?: string;
  language: Language;
  seoLanguage: Language;
  additionalSeoLanguage: Language[];
  freemium: boolean;
  freemiumMaxProductsPerCategory?: number;
  freemiumMaxTotalProducts?: number;
  hubspotToken?: string;
  vtexAppKey?: string;
  vtexAppToken?: string;
  vtexUrl?: string;
  vtexSalesChannel?: number;
  vtexAffiliateId?: string;
  shopifyUrl?: string;
  shopifyAccessToken?: string;
  group?: string;
}

export interface UpdateCustomerInfoDto {
  id: number;
  name?: string;
  freemium?: boolean;
  status?: boolean;
}

export interface StoreConfig {
  id: number;
  publicId: string;
  name: string;
  platform: string; // Ex: "NONE", "VTEX"
  url?: string;
  language: string; // Ex: "EN-US"
  seoLanguage: string;
  additionalSeoLanguage: string[]; // Array de idiomas adicionais
  categories: string; // Categoria da loja, pode ser uma string simples ou um array
  fetchedProducts: boolean;
  taxonomyVersion: string;
  visionCategoriesAvailable: string[] | null;
  colors: string;
  collections: string[] | null;
  filterColors: string[] | null;
  colorFamilies: string[] | null;
  brands: string[] | null;
  sizes: string[] | null;
  logoUrl: string | null;
  vtexAppKey: string | null;
  vtexAppToken: string | null;
  vtexUrl: string | null;
  vtexAffiliateUrl: string | null;
  vtexAffiliateId: string | null;
  vtexSalesChannel: string | null;
  freemium: boolean;
  freemiumFetchedProducts: boolean;
  freemiumMaxProductsPerCategory: number;
  freemiumMaxTotalProducts: number;
  needUpdateCategoryName: boolean;
  fetchedSales: boolean;
  meta_user_token: string | null;
  meta_page_id: string | null;
  meta_ad_account_id: string | null;
  meta_business_id: string | null;
  meta_catalog_id: string | null;
  similaritySearchCount: number | null;
  hubspotToken: string | null;
  visionAutoRun: boolean;
  visionLastSubmission: string | null;
  vtexNotificationsCount: number;
  omnichatApiKey: string | null;
  omnichatApiSecret: string | null;
}
export interface CreateCustomerDtoWithStoreConfig extends CreateCustomerDto {
  storeConfig: StoreConfig;
}

export interface CreateCustomerResponse {
  created: boolean;
}

export interface UpdateStoreURLDto {
  storeURL: string;
}

export interface UpdateVtexCredentialsDto {
  vtexUrl: string;
  vtexAppKey: string;
  vtexAppToken: string;
  vtexSalesChannel: number;
  vtexAffiliateId: string;
}

export interface UpdateShopifyCredentialsDto {
  shopifyUrl: string;
  shopifyAccessToken: string;
}

export interface UpdateShopifyCredentialsResponse {
  success: boolean;
  message: string;
}

export interface UpdateShopifyAdditionalSettingsDto {
  shopifyPublicationIds?: string | null;
  shopifyExcludedVendors?: string | null;
  shopifyOrderSource?: string | null;
}

export interface UpdateShopifyAdditionalSettingsResponse {
  success: boolean;
  message: string;
}

export interface UpdateVtexCredentialsResponse {
  updated: boolean;
}

export interface UpdateHubspotTokenDto {
  hubspotToken: string;
}

export interface UpdateHubspotTokenResponse {
  updated: boolean;
}

export interface UpdateOmnichatCredentialsDto {
  omnichatApiKey: string;
  omnichatApiSecret: string;
}

export interface UpdateOmnichatCredentialsResponse {
  updated: boolean;
}

export interface GenerateVtexAffiliateURLResponse {
  vtexAffiliateUrl: string;
}

export interface VisionTriggerDto {
  visionAutoRun: boolean;
}

export interface VisionTriggerResponse {
  visionAutoRun: boolean;
}

export interface CategoriesSeoResponse {
  _id: string;
  customer_id: string;
  category_id: string;
  name: string;
  description: string;
  keywords: string;
  language: string;
  title: string;
}

export interface GetCustomersInfoResponse {
  id: number;
  name: string;
}

export interface CustomersQueryFilterDto {
  page: number;
  limit: number;
  searchTerm?: string;
  platform?: Platform;
  status?: boolean;
}

export interface GetAllCustomersDto extends StoreConfig {
  group?: string;
  status?: boolean;
  shopifyUrl?: string;
  shopifyAccessToken?: string;
  metrics: Metrics;
}

export interface Metrics {
  progress: number;
  recommendationImplemented: boolean;
  ecommerceData: boolean;
  taxonomy: boolean;
  productRead: boolean;
  mediaCluster: boolean;
  seoIntegrated: boolean;
}

export interface GetAllCustomersResponse {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  items: Partial<GetAllCustomersDto>[];
}

export interface MetricsTotalizersResponse {
  totalCustomers: {
    total: number;
    thisMonth: number;
  };
  completionRate: number;
  completedCustomers: number;
  mostCriticalStep: {
    name: string;
    rate: number;
  };
}

export interface CategorySeoCSVResponse {
  filename: string;
  url: string;
}

export interface UpdateVersionDto {
  version: string;
}

export interface UploadLogoDto {
  filename: string;
  contentType: string;
}

export interface UploadLogoResponse {
  filename: string;
  url: string;
}

class CustomerService {
  constructor() {}

  async create(createCustomerDto: CreateCustomerDto) {
    const { data } = await httpClient.post<CreateCustomerResponse>(
      '/v1/customer/create',
      createCustomerDto,
    );

    return data;
  }

  async getAllCustomers(customersQueryFilterDto: CustomersQueryFilterDto) {
    const { data } = await httpClient.get<GetAllCustomersResponse>(
      '/v1/customer/find-all-v2',
      {
        params: customersQueryFilterDto,
      },
    );

    return data;
  }

  async getMetricsTotalizers() {
    const { data } = await httpClient.get<MetricsTotalizersResponse>(
      '/v1/customer/metrics-totalizers',
    );

    return data;
  }

  async updateCustomer(updateCustomerDto: UpdateCustomerInfoDto) {
    const { data } = await httpClient.patch<UpdateCustomerInfoDto>(
      '/v1/customer/update-customer',
      updateCustomerDto,
    );

    return data;
  }

  async updateStoreURL(updateStoreURLDto: UpdateStoreURLDto) {
    const { data } = await httpClient.patch(
      '/v1/customer/update-store-url',
      updateStoreURLDto,
    );

    return data;
  }

  async updateVtexCredentials(
    updateVtexCredentialsDto: UpdateVtexCredentialsDto,
  ) {
    const { data } = await httpClient.patch<UpdateVtexCredentialsResponse>(
      '/v1/customer/update-vtex-credentials',
      updateVtexCredentialsDto,
    );

    return data;
  }

  async updateShopifyCredentials(
    updateShopifyCredentialsDto: UpdateShopifyCredentialsDto,
  ) {
    const { data } = await httpClient.patch<UpdateShopifyCredentialsResponse>(
      '/v1/customer/shopify/credentials',
      updateShopifyCredentialsDto,
    );

    return data;
  }

  async updateShopifyAdditionalSettings(
    dto: UpdateShopifyAdditionalSettingsDto,
  ) {
    const { data } =
      await httpClient.patch<UpdateShopifyAdditionalSettingsResponse>(
        '/v1/customer/shopify/additional-settings',
        dto,
      );

    return data;
  }

  async updateHubspotToken(updateHubspotTokenDto: UpdateHubspotTokenDto) {
    const { data } = await httpClient.patch<UpdateHubspotTokenResponse>(
      '/v1/customer/update-hubspot-token',
      updateHubspotTokenDto,
    );

    return data;
  }

  async updateOmnichatCredentials(
    updateOmnichatCredentialsDto: UpdateOmnichatCredentialsDto,
  ) {
    const { data } = await httpClient.patch<UpdateOmnichatCredentialsResponse>(
      '/v1/customer/update-omnichat-credentials',
      updateOmnichatCredentialsDto,
    );

    return data;
  }

  async generateVtexAffiliateURL() {
    const { data } = await httpClient.patch<GenerateVtexAffiliateURLResponse>(
      '/v1/customer/generate-vtex-affiliate-url',
      null,
    );

    return data;
  }

  async visionTrigger(visionTriggerDto: VisionTriggerDto) {
    const { data } = await httpClient.post<VisionTriggerResponse>(
      '/v1/customer/vision-trigger',
      visionTriggerDto,
    );

    return data;
  }

  async getCategoriesSeo() {
    const { data } = await httpClient.get<CategoriesSeoResponse[]>(
      '/v1/customer/get-categories-seo',
    );

    return data;
  }

  async getCustomersInfo() {
    const { data } = await httpClient.get<GetCustomersInfoResponse[]>(
      '/v1/customer/get-customers-info',
    );

    return data;
  }

  async getCategorySeoCSV() {
    const { data } = await httpClient.get<CategorySeoCSVResponse>(
      '/v1/customer/get-categories-seo-csv',
    );

    return data;
  }

  async getCustomerById(id: number) {
    const { data } = await httpClient.get<GetAllCustomersDto>(
      `/v1/customer/find-one/${id}`,
    );
    return data;
  }

  async updateTaxonomyVersion(updateVersionDto: UpdateVersionDto) {
    const { data } = await httpClient.patch(
      '/v1/customer/taxonomy/version',
      updateVersionDto,
    );
    return data;
  }

  async uploadLogo(uploadLogoDto: UploadLogoDto) {
    const { data } = await httpClient.post<UploadLogoResponse>(
      '/v1/customer/logo',
      uploadLogoDto,
    );
    return data;
  }

  async verifyCreateNameModel() {
    const { data } = await httpClient.get<boolean>(
      '/v1/customer/verify-create-name-model',
    );

    return data;
  }
}

export default new CustomerService();
